import {
  canInvite,
  GuildEventSchema,
  GuildInvitedSchema,
  GuildRejectedSchema,
  type GuildState,
  GuildStateSchema,
} from '@shefira/shared';

import type { ChatPanel } from './chat.js';
import { GUILD_RANK_OF_ACTION, type GuildCommand } from './chat-commands.js';
import { GuildPanel } from './guild-panel.js';
import { GUILD_REFUSAL_TEXT, guildEventText } from './guild-text.js';
import { InvitePrompt, type InviteQuestion } from './invite-prompt.js';
import { uiFormat } from './text.js';

/** The little of a Colyseus room this needs. */
interface GuildRoom {
  send: (type: string, payload?: unknown) => void;
  onMessage: (type: string, callback: (payload: unknown) => void) => unknown;
}

/** The question window an invitation opens; `from` carries the guild's id. */
export interface GuildPrompt {
  show: (question: InviteQuestion) => void;
  close: () => void;
}

export interface GuildClientOptions {
  /** The local character's id, read when needed: it arrives after the join. */
  selfId?: () => string | undefined;
  /**
   * The window that asks « accepter ? »; by default an `InvitePrompt` of its
   * own. The answer arrives with the guild's id in `from`.
   */
  prompt?: (onAnswer: (guildId: string, accept: boolean) => void) => GuildPrompt;
}

/**
 * The client's side of the guilds (ADR 0036): shows what the server says —
 * the window, the invitations, what happened — and turns clicks and chat
 * lines into intentions. It keeps nothing of its own beyond the last state
 * sent, read to find a member's id from their name.
 */
export class GuildClient {
  /** The guild as the server last sent it; null without one. */
  state: GuildState = null;
  readonly panel: GuildPanel;
  private readonly prompt: GuildPrompt;

  constructor(
    private readonly room: GuildRoom,
    private readonly chat: ChatPanel,
    options: GuildClientOptions = {},
  ) {
    this.panel = new GuildPanel({
      onCreate: (name, tag) => {
        room.send('guild_create', { name, tag });
      },
      onInvite: (name) => {
        this.invite(name);
      },
      onLeave: () => {
        room.send('guild_leave', {});
      },
      onKick: (characterId) => {
        room.send('guild_kick', { characterId });
      },
      onRank: (characterId, rank) => {
        room.send('guild_rank', { characterId, rank });
      },
      onMotd: (text) => {
        room.send('guild_motd', { text });
      },
      onDisband: () => {
        room.send('guild_disband', {});
      },
      onMessage: (name) => {
        chat.setTarget({ channel: 'private', to: name });
        chat.focus();
      },
      ...(options.selfId !== undefined && { selfId: options.selfId }),
    });
    const answer = (guildId: string, accept: boolean): void => {
      room.send('guild_answer', { guildId, accept });
    };
    this.prompt = options.prompt?.(answer) ?? new InvitePrompt(answer);

    room.onMessage('guild', (payload) => {
      const parsed = GuildStateSchema.safeParse(payload);
      if (!parsed.success) return;
      this.state = parsed.data;
      this.panel.setGuild(parsed.data);
      // Out of the guild, the chat stops speaking to it.
      if (
        (parsed.data === null || parsed.data.members.length === 0) &&
        chat.current.channel === 'guild'
      ) {
        chat.setTarget({ channel: 'zone' });
      }
    });
    room.onMessage('guild_invited', (payload) => {
      const parsed = GuildInvitedSchema.safeParse(payload);
      if (!parsed.success) return;
      const { guildId, guildName, tag, from, expiresInMs } = parsed.data;
      this.prompt.show({
        from: guildId,
        text: uiFormat("{from} t'invite dans la guilde {guild} [{tag}].", {
          from,
          guild: guildName,
          tag,
        }),
        expiresInMs,
      });
    });
    room.onMessage('guild_event', (payload) => {
      const parsed = GuildEventSchema.safeParse(payload);
      if (parsed.success) chat.notice(guildEventText(parsed.data));
    });
    room.onMessage('guild_rejected', (payload) => {
      const parsed = GuildRejectedSchema.safeParse(payload);
      if (parsed.success) chat.notice(GUILD_REFUSAL_TEXT[parsed.data.reason]);
    });
  }

  /** Whether my rank lets me invite: the player menu offers it then. */
  canInvite(): boolean {
    return this.state !== null && canInvite(this.state.myRank);
  }

  invite(name: string): void {
    this.room.send('guild_invite', { name });
  }

  /** A `/guilde …` line from the chat. */
  run(command: GuildCommand): void {
    switch (command.action) {
      case 'open':
        this.panel.open();
        return;
      case 'invite':
        this.invite(command.name);
        return;
      case 'leave':
        this.room.send('guild_leave', {});
        return;
      case 'disband':
        this.room.send('guild_disband', {});
        return;
      case 'motd':
        this.room.send('guild_motd', { text: command.text });
        return;
      case 'kick': {
        const member = this.memberNamed(command.name);
        if (member !== undefined) this.room.send('guild_kick', { characterId: member.id });
        return;
      }
      case 'promote':
      case 'demote': {
        const member = this.memberNamed(command.name);
        if (member === undefined) return;
        this.room.send('guild_rank', {
          characterId: member.id,
          rank: GUILD_RANK_OF_ACTION[command.action],
        });
        return;
      }
    }
  }

  /** The member of that name in the last state, or a notice in the chat. */
  private memberNamed(name: string): { id: string } | undefined {
    const wanted = name.toLowerCase();
    const member = this.state?.members.find((m) => m.name.toLowerCase() === wanted);
    if (member === undefined) {
      this.chat.notice(
        this.state === null
          ? "Tu n'es pas dans une guilde."
          : uiFormat("{name} n'est pas dans ta guilde.", { name }),
      );
    }
    return member;
  }

  destroy(): void {
    this.panel.destroy();
    this.prompt.close();
  }
}
